import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../component/sidebar/sidebar";
import Layout from "../../component/layout/layout";
import { IoAdd } from "react-icons/io5";
import { MdLocalMovies } from "react-icons/md";
import {
  seasonUpdate,
  addNewSeason,
  SeasonShowApi,
  GenresList,
  SeriesShowApi,
} from "../../api/HttpApis";
import style from "./AddSeasons.module.css";
import useCommonRating from "../../component/apiCall/RatingandGenre";
import Button from "../../component/button/Button";
import { showToast } from "../../utils/Toastify/Toastify";
import SkeletonLoader from "../../component/skeletonLoader/SkeletonLoader";

// Import Accordion Components
import BasicInformation from "../../component/Information/BasicInformation";
import MediaAssets from "../../component/Information/MediaAssests";
import TrailerClips from "../../component/Information/TrailerClips";
import CastCrewMembers from "../../component/Information/CastCrewMembers";
import Technical from "../../component/Information/Technical";
import Publishing from "../../component/Information/Publishing";
import ContentRightsConfirmation, { isCreator, CONTENT_RIGHTS_ERROR } from "../../component/Information/ContentRightsConfirmation";
import {
  appendAccessPayload,
  getAccessFields,
  mapPublishingFromApi,
} from "../../component/Information/PublishingPayload";

const AddSeasons = () => {
  const navigate = useNavigate();
  const { seriesId, seasonId } = useParams();
  const isEditing = Boolean(seasonId);
  const { ratingOptions, accessOptions } = useCommonRating();

  // Accordion State
  const [activeAccordion, setActiveAccordion] = useState("media");
  const soloProps = {
    activeId: activeAccordion,
    setActiveId: setActiveAccordion,
  };

  // UI States
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [showRightsError, setShowRightsError] = useState(false);
  const [loadingState, setLoadingState] = useState({
    Publish: false,
    draft: false,
  });
  const [userUploadedMedia, setUserUploadedMedia] = useState(false);

  // Media States
  const [imagePreviewURL, setImagePreviewURL] = useState(null);
  const [bannerPreviewURL, setBannerPreviewURL] = useState(null);
  const [heroPreviewURL, setHeroPreviewURL] = useState(null);
  const [logoPreviewURL, setLogoPreviewURL] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isPortraitUploaded, setIsPortraitUploaded] = useState(false);
  const [isLandscapeUploaded, setIsLandscapeUploaded] = useState(false);
  const [activeImageType, setActiveImageType] = useState(null);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [video, setVideo] = useState(null);
  const [trailer, setTrailer] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [subtitleFiles, setSubtitleFiles] = useState([]);

  // Metadata States
  const [genresDataList, setGenresDataList] = useState([]);
  const [seasonDetails, setSeasonDetails] = useState(null);
  const [seriesTitle, setSeriesTitle] = useState("Series Name");
  // A season's access (Free/Paid, Subscription/Buy/Rent, pricing) always mirrors its
  // series and can't be changed here. null until the series loads; Save is blocked
  // without it so a season can never be saved with its own access.
  const [seriesAccess, setSeriesAccess] = useState(null);

  // Form Data
  const [formData, setFormData] = useState({
    name: "",
    rating: "",
    description: "",
    year: "",
    portrait: null,
    landscape: null,
    status: "",
    access: "",
    season_number: "",
    genres: "",
  });

  // Lifted States for Sub-components
  const [technicalData, setTechnicalData] = useState({
    max_quality: "", frame_rate: "", hdr: "", aspect_ratio: "", drm: "", max_streams: "", geo_restrictions: "",
  });
  const [publishingData, setPublishingData] = useState({
    publishStatus: "", access: "", access_type: "", rent_price: "", rent_duration_days: "", buy_price: "", currency: "", publishDateTime: "", contentTier: "", ppvPrice: "", licenseExpiry: "", featuredPlacement: "Not Featured", featuredUntil: "",
  });
  const [crewData, setCrewData] = useState({ directors: "", writers: "", producers: "", composers: "" });
  const [castMembers, setCastMembers] = useState([]);
  const [ratingsData, setRatingsData] = useState({ imdb: "", rottenTomatoes: "", awards: "" });
  const [trailersList, setTrailersList] = useState([]);

  const portraitInputRef = useRef();
  const landscapeInputRef = useRef();
  const seasonsBannerImage = useRef(null);
  const seasonsPortraitImage = useRef(null);
  const activeImageTypeRef = useRef(activeImageType);
  useEffect(() => {
    activeImageTypeRef.current = activeImageType;
  }, [activeImageType]);
  const isFilled = (...fields) => fields.every(Boolean);
  // Helper for progress bar
  useEffect(() => {
    if (loadingState.Publish || loadingState.draft) {
      const interval = setInterval(() => {
        setProgress((prev) => (prev >= 99 ? prev : prev + 1));
      }, 30);
      return () => clearInterval(interval);
    }
  }, [loadingState]);
  useEffect(() => {
    if (!seriesId) return;
    const fetchSeriesTitle = async () => {
      try {
        const res = await SeriesShowApi(seriesId);
        const series = res?.data?.data || res?.data;
        setSeriesTitle(series?.name || "Series Name");
        if (series) setSeriesAccess(getAccessFields(series));
      } catch (err) {
        console.log(err);
        showToast("Couldn't load the series' access. Reload the page before saving.", "error");
      }
    };
    fetchSeriesTitle();
  }, [seriesId]);

  // Re-applied after the season loads, since that overwrites publishingData.
  useEffect(() => {
    if (!seriesAccess) return;
    setPublishingData((prev) => ({ ...prev, ...seriesAccess }));
  }, [seriesAccess, seasonDetails]);

  // Fetch Genres
  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const res = await GenresList();
        setGenresDataList(res?.data?.data || []);
      } catch (err) { showToast("Failed to fetch genres", "error"); }
    };
    fetchGenres();
  }, []);

  // Fetch Season Data
  useEffect(() => {
    if (!seasonId) return;
    const fetchSeasonDetails = async () => {
      try {
        setLoadingState((prev) => ({ ...prev, draft: true }));
        const res = await SeasonShowApi(seriesId, seasonId);
        const data = res?.data?.data || res?.data;

        if (data) {
          setSeasonDetails(data);
          seasonsBannerImage.current = data.landscape || data.landscape_url;
          seasonsPortraitImage.current = data.portrait || data.portrait_url;
          setFormData({
            name: data.name || "",
            rating: data.rating || "",
            description: data.description || "",
            status: data.status || "",
            year: data.year ?? data.year,
            // access: data.access || "",
            season_number: data.season_number || "",
            portrait: data.portrait || null,
            landscape: data.landscape || null,
            genres: Array.isArray(data.genres) ? data.genres[0] : (data.genres || ""),
          });
          setPublishingData((prev) => ({
            ...prev,
            ...mapPublishingFromApi(data, prev),
            status: data.status ?? prev.status,
          }));

          if (data.portrait || data.portrait_url) {
            setImagePreviewURL(data.portrait || data.portrait_url);
            setIsPortraitUploaded(true);
          }
          if (data.landscape || data.landscape_url) {
            setBannerPreviewURL(data.landscape || data.landscape_url);
            setIsLandscapeUploaded(true);
          }
          if (data.trailer) setTrailer(data.trailer);
          if (data.video) setVideo(data.video);
          const rawCast = data.casts || data.cast_crew?.cast || [];
          const rawCrew = data.crews || data.cast_crew?.crew || [];

          // Cast/Crew refactor — actor management:
          // GET response now includes a nested `member` object on each cast/crew row.
          // We rebuild castMembers state from member_id + the inline member data — no extra API lookup needed.
          // Crew rows are grouped by member_id so a person with multiple roles becomes one entry with a role array.
          if (rawCast.length || rawCrew.length) {
            let nextId = Date.now();

            const castEntries = rawCast.map((c) => ({
              id: nextId++,
              type: "Cast",
              actorId: c.member_id,
              actorName: c.member?.name || "",
              image_url: c.member?.image_url || "",
              role: c.role || "ACTOR",
              character: c.character_name || "",
            }));

            const crewByMember = rawCrew.reduce((acc, c) => {
              if (!acc[c.member_id]) {
                acc[c.member_id] = { member: c.member || {}, roles: [] };
              }
              if (c.role) acc[c.member_id].roles.push(c.role);
              return acc;
            }, {});

            const crewEntries = Object.entries(crewByMember).map(
              ([memberId, { member, roles }]) => ({
                id: nextId++,
                type: "Crew",
                actorId: Number(memberId),
                actorName: member?.name || "",
                image_url: member?.image_url || "",
                role: roles,
                character: "",
              })
            );

            setCastMembers([...castEntries, ...crewEntries]);
          }
        }
      } catch (err) {
        showToast("Failed to load season details.", "error");
      } finally {
        setLoadingState((prev) => ({ ...prev, draft: false }));
      }
    };
    fetchSeasonDetails();
  }, [seriesId, seasonId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleIconClick = (type) => {
    setActiveImageType(type);
    activeImageTypeRef.current = type;
    setIsOpen(true);
  };

  const handleimagesvalue = (item) => {
    if (!item) return;
    const type = activeImageTypeRef.current;
    if (!type) return;
    setUserUploadedMedia(true);
    if (type.startsWith("trailer-video-")) {
      const trailerId = parseInt(type.replace("trailer-video-", ""));
      setTrailersList((prev) =>
        prev.map((t) => (t.id === trailerId ? { ...t, videoFile: item } : t))
      );
    } else if (type === "portrait") {
      setIsPortraitUploaded(true);
      setImagePreviewURL(item);
      setFormData((prev) => ({ ...prev, portrait: item?.id ?? null }));
    } else if (type === "landscape") {
      setIsLandscapeUploaded(true);
      setBannerPreviewURL(item);
      setFormData((prev) => ({ ...prev, landscape: item?.id ?? null }));
    } else if (type === "logo") {
      setLogoPreviewURL(item);
    } else if (type === "hero") {
      setHeroPreviewURL(item);
    } else if (type === "video") {
      setVideo(item);
    }
  };

  const handleSelectVideos = (item) => {
    if (activeImageType === "video") setVideo(item);
    else if (activeImageType === "hero") setHeroPreviewURL(item);
    else setSelectedVideos(item);
  };

  const handleSubmit = async (e, type, buttonType) => {
    e.preventDefault();

    if (isCreator() && !rightsConfirmed) {
      setShowRightsError(true);
      showToast(CONTENT_RIGHTS_ERROR, "error");
      return;
    }
    if (!formData.name) { showToast("Name is required", "error"); return; }
    if (!seriesAccess) {
      showToast("Couldn't load the series' access. Reload the page and try again.", "error");
      return;
    }

    const payload = new FormData();

    /* IMAGES */
    payload.append(
      "portrait",
      imagePreviewURL?.bunny_cdn_url ||
      imagePreviewURL?.id ||
      seasonsPortraitImage.current ||
      ""
    );
    payload.append(
      "landscape",
      bannerPreviewURL?.bunny_cdn_url ||
      bannerPreviewURL?.id ||
      seasonsBannerImage.current ||
      ""
    );

    // payload.append("status", type);

    /* MEDIA */
    if (trailer?.url || trailer?.bunny_cdn_url || seasonDetails?.trailer?.url) {
      payload.append(
        "trailer",
        trailer?.url || trailer?.bunny_cdn_url || seasonDetails?.trailer?.url || ""
      );
    }

    /* VIDEO — always send key */
    payload.append("video", video?.id || "");

    /* CAST & CREW */
    const castsPayload = (castMembers || [])
      .filter((c) => c.type === "Cast" && c.actorId)
      .map((c) => ({
        member_id: c.actorId,
        role: c.role || "ACTOR",
        character_name: c.character || "",
      }));
    const crewsPayload = (castMembers || [])
      .filter((c) => c.type === "Crew" && c.actorId)
      .flatMap((c) => {
        const roles = Array.isArray(c.role) ? c.role : c.role ? [c.role] : [];
        return roles.map((r) => ({ member_id: c.actorId, role: r }));
      });
    payload.append("casts", JSON.stringify(castsPayload));
    payload.append("crews", JSON.stringify(crewsPayload));

    /* SUBTITLES */
    const subtitlesMeta = [];
    trailersList.forEach((t) => {
      (t.subtitleFiles || []).forEach((file, idx) => {
        if (file) {
          subtitlesMeta.push({
            language: file.language || (idx === 0 ? "en" : idx === 1 ? "hi" : "other"),
            format: file.name.endsWith(".vtt") ? "vtt" : "srt",
          });
          payload.append("subtitles", file);
        }
      });
    });
    (subtitleFiles || []).forEach((file, idx) => {
      if (file) {
        subtitlesMeta.push({
          language: file.language || (idx === 0 ? "en" : idx === 1 ? "hi" : "other"),
          format: file.name.endsWith(".vtt") ? "vtt" : "srt",
        });
        payload.append("subtitles", file);
      }
    });
    if (subtitlesMeta.length > 0) {
      payload.append("subtitles_meta", JSON.stringify(subtitlesMeta));
    }

    /* LOGO — always send key */
    const logoUrl =
      logoPreviewURL    // will be null after removal
        ? (logoPreviewURL?.title_logo_url || logoPreviewURL?.bunny_cdn_url ||
          logoPreviewURL?.url || (typeof logoPreviewURL === "string" ? logoPreviewURL : null) ||
          seasonDetails?.title_logo_url || "")
        : "";           // cleared — send empty string
    payload.append("title_logo_url", logoUrl);

    const heroUrl =
      heroPreviewURL  
        ? (heroPreviewURL?.preview_url || heroPreviewURL?.bunny_cdn_url ||
          heroPreviewURL?.HLSPlaylistURL || (typeof heroPreviewURL === "string" ? heroPreviewURL : null) ||
          seasonDetails?.preview_url || "")
        : "";         
    payload.append("preview_url", heroUrl);

    /* BASIC INFO FIELDS — always send key, empty string if no value */
    const BASIC_INFO_KEYS = [
      "description", "year", "rating", "content_warning", "language", "country",
      "tagline", "studio", "imdb_id", "season_number","drm",
    ];

    for (const key of BASIC_INFO_KEYS) {
      const val = formData[key];
      const isEmpty = val === null || val === undefined || val === "" || String(val) === "undefined";
      payload.append(key, isEmpty ? "" : val);
    }

    // name — always send, empty if no value
    const nameVal = formData["name"];
    const nameIsEmpty = nameVal === null || nameVal === undefined || nameVal === "" || String(nameVal) === "undefined";
    payload.append("name", nameIsEmpty ? "" : nameVal);

    /* REMAINING formData — skip keys already handled */
    const ALREADY_HANDLED = new Set([
      ...BASIC_INFO_KEYS,
      "name", "status", "portrait", "landscape",
      "genres", "badges", "access",
    ]);

    for (const key in formData) {
      if (ALREADY_HANDLED.has(key)) continue;
      if (
        formData[key] === null || formData[key] === "" || formData[key] === undefined ||
        String(formData[key]) === "undefined"
      ) continue;
      payload.append(key, formData[key]);
    }

    /* GENRES */
    if (formData.genres) {
      const selectedGenreNames = Array.isArray(formData.genres)
        ? formData.genres
        : formData.genres.split(",").map((g) => g.trim()).filter(Boolean);
      const genreIds = selectedGenreNames
        .map((name) => {
          const match = genresDataList?.find((g) => g.name.toLowerCase() === name.toLowerCase());
          return match ? match.id : null;
        })
        .filter((id) => id !== null);
      payload.append("genre_ids", JSON.stringify(genreIds));
    }

    /* BADGE — always send key */
    const badgeValue = formData.badges
      ? (Array.isArray(formData.badges) ? formData.badges[0] : formData.badges)
      : "";
    payload.append("badge", String(badgeValue));

    /* TECHNICAL — always send all keys */
    payload.append("drm", technicalData.drm || "");
    payload.append("geo_restrictions", technicalData.geo_restrictions || "");

    /* PUBLISHING — always send all keys */
    payload.append("publish_status", publishingData.publishStatus || "");
    appendAccessPayload(payload, { ...publishingData, ...seriesAccess });
    payload.append("publish_date_time", publishingData.publishDateTime || "");


    /* CREW — always send all keys */
    payload.append("producers", crewData.producers || "");
    payload.append("composers", crewData.composers || "");

    /* RATINGS — always send all keys */
    payload.append("imdb_rating", ratingsData.imdb || "");

    /* TRAILERS */
    if (trailersList.length > 0) {
      const firstTrailer = trailersList[0];
      const trailerApiId = firstTrailer.videoFile?.id || firstTrailer.id;
      if (trailerApiId) payload.append("trailer", trailerApiId);
    }

    /* SEASON-SPECIFIC: series_id — always send key */
    payload.append("series_id", seriesId || "");

    setLoadingState((prev) => ({ ...prev, [buttonType]: true }));
    try {
      if (seasonId) {
        await seasonUpdate(seriesId, seasonId, payload);
        showToast("Season updated successfully!", "success");
      } else {
        await addNewSeason(seriesId, payload);
        showToast("Season added successfully!", "success");
      }
      navigate(`/mainlayout/seasons/${seriesId}`);
    } catch (error) {
      showToast(error?.response?.data?.message || "Operation failed", "error");
    } finally {
      setLoadingState((prev) => ({ ...prev, [buttonType]: false }));
    }
  };

  return (
    <div className={style.mainContainer}>
      <div className={style.innerContainer}>
        <div className={style.leftContainer}>
          <div className={style.seriesNameBox}>
            {seriesTitle}
          </div>
           <MediaAssets
            formData={formData}
            handleChange={handleChange}
            handleIconClick={handleIconClick}
            isLandscapeUploaded={isLandscapeUploaded}
            isPortraitUploaded={isPortraitUploaded}
            bannerPreviewURL={bannerPreviewURL}
            imagePreviewURL={imagePreviewURL}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            setSelectedVideos={handleSelectVideos}
            handleimagesvalue={handleimagesvalue}
            setIsPortraitUploaded={setIsPortraitUploaded}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            portraitInputRef={portraitInputRef}
            selectedVideos={selectedVideos}
            setTrailer={setTrailer}
            setVideo={setVideo}
            video={video}
            soloProps={soloProps}
            onSubtitleChange={setSubtitleFiles}
            onLogoChange={setLogoFile}
            heroPreviewURL={heroPreviewURL}
            logoPreviewURL={logoPreviewURL}
            onLogoRemove={() => setLogoPreviewURL(null)}
            onHeroRemove={() => setHeroPreviewURL(null)}
          />
          <BasicInformation
            formData={formData}
            handleChange={handleChange}
            soloProps={soloProps}
          />

          <TrailerClips
            movieData={seasonDetails}
            selectedVideos={selectedVideos}
            setSelectedVideos={setSelectedVideos}
            setTrailer={setTrailer}
            trailersList={trailersList}
            setTrailersList={setTrailersList}
            soloProps={soloProps}
            contentTitle={formData.name}
          />
          <CastCrewMembers
            movieData={seasonDetails}
            crewData={crewData}
            setCrewData={setCrewData}
            castMembers={castMembers}
            setCastMembers={setCastMembers}
            ratingsData={ratingsData}
            setRatingsData={setRatingsData}
            soloProps={soloProps}
          />
          <Publishing
            soloProps={soloProps}
            publishingData={publishingData}
            setPublishingData={setPublishingData}
            lockAccess
          />

          {/* --- BUTTON SECTION --- */}
          <ContentRightsConfirmation
            checked={rightsConfirmed}
            onChange={(v) => {
              setRightsConfirmed(v);
              if (v) setShowRightsError(false);
            }}
            showError={showRightsError}
          />

          <div className={style.buttonWrapper}>

            <div className={style.readyToGoLiveBanner}>
              <div className={style.readyToGoLiveLeft}>
                <span className={style.readyToGoLiveTitle}>Ready To Go Live?</span>
                <span className={style.readyToGoLiveMeta}>
                  {formData.name}
                </span>
              </div>
              <div className={style.btnBox}>
                <Button
                  className={style.publishNowBtn}
                  onClick={(e) => handleSubmit(e, publishingData.status, "Publish")}
                  loading={loadingState.Publish}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
          {/* --- END BUTTON SECTION --- */}

        </div>
      </div>
    </div>
  );
};

export default AddSeasons;